/**
 * Rehype guard that decides, PER TAG, what counts as raw HTML.
 *
 * Must run BEFORE `rehypeRaw`. At that point in the pipeline, anything
 * markdown recognized as inline/block HTML syntax exists as hast `raw` nodes
 * (code spans and fenced blocks are already `code` ELEMENTS, so their angle
 * brackets never appear in raw nodes and are never touched). This plugin
 * walks the raw nodes and entity-escapes every tag whose name is not on the
 * allowlist, so `rehypeRaw`'s HTML parser renders those as literal text
 * instead of parsing them into elements that `rehypeSanitize` would then
 * silently drop.
 *
 * The three-way rule:
 *   - RENDER  — real, safe HTML tags (the `rehypeSanitize` allowlist:
 *               `<p>`, `<strong>`, `<ul>`, `<span>`, …). Left raw so they
 *               parse and render. Plate-authored note bodies depend on this.
 *   - PASS    — the entity-mention tag family we control (`<Entity>`,
 *               `<NamedEntity>`, `<mention>`). Well-formed mentions are
 *               converted to allowlisted spans by `preprocessEntityTags`
 *               before parsing ever happens; anything malformed that reaches
 *               this plugin is left raw so `rehypeSanitize` degrades it to
 *               its display-name text — NOT escaped, which would surface the
 *               full ugly markup to users.
 *   - ESCAPE  — everything else. Pseudo-tags in prose (`Array<string>`,
 *               `Promise<int>`) and dangerous tags (`<script>`) become
 *               visible literal text before the HTML parser ever sees them.
 *
 * Why per-tag instead of per-content: block-level HTML arrives as a SINGLE
 * raw node containing multiple tags (`<p>Array<string></p>`), so any
 * whole-node or whole-content decision either mangles the safe tags or
 * renders the unsafe ones. Two prior whole-content approaches each caused a
 * user-visible regression (see MarkdownContent.rawHtmlGate.test.tsx).
 *
 * Known limitation (deliberate, matches GitHub-flavored markdown): a generic
 * parameter whose name IS a real HTML tag (`List<a>`, `Vec<b>`) is
 * indistinguishable from HTML and renders as an element.
 */

import { entitySanitizeSchema } from './entitySanitize';

/**
 * Tags that are parsed and rendered as HTML. Sourced from the sanitize
 * schema so the guard and the sanitizer can never disagree: every tag the
 * guard lets through either renders (allowlisted) or is a mention tag the
 * sanitizer intentionally degrades.
 */
const RENDERABLE_TAGS: ReadonlySet<string> = new Set(
  (entitySanitizeSchema.tagNames ?? []).map(tag => tag.toLowerCase())
);

/** Mention tag family — see PASS in the module doc. */
const MENTION_TAGS: ReadonlySet<string> = new Set([
  'entity',
  'namedentity',
  'mention',
]);

/**
 * Matches one HTML-ish tag: `<name …>`, `</name>`, or `<name …/>`.
 * Tag names are letter-initial per the HTML spec (micromark applies the same
 * rule when deciding what becomes a raw node, so this stays in sync with
 * what can actually reach us). Attribute scanning is the pragmatic `[^>]*`;
 * a quoted `>` inside an attribute of a NON-allowlisted tag could mis-span,
 * but non-allowlisted tags are prose pseudo-tags that don't carry quoted
 * attributes in practice.
 */
const TAG_PATTERN = /<\/?([a-zA-Z][a-zA-Z0-9-]*)[^>]*?\/?>/g;

/**
 * Entity-escape every tag in `html` whose name is neither renderable nor a
 * mention tag. Escaped tags survive `rehypeRaw`'s parse as literal text
 * (the parser decodes `&lt;`/`&gt;` back to visible angle brackets).
 *
 * Exported for direct unit testing; production use is via the plugin below.
 */
export function escapeUnknownRawTags(html: string): string {
  return html.replace(TAG_PATTERN, (match, tagName: string) => {
    const name = tagName.toLowerCase();
    if (RENDERABLE_TAGS.has(name) || MENTION_TAGS.has(name)) {
      return match;
    }
    return match.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  });
}

/** Minimal structural view of hast nodes — enough to walk and edit raws. */
interface WalkableNode {
  type?: string;
  value?: string;
  children?: WalkableNode[];
}

/**
 * The rehype plugin. Order it immediately before `rehypeRaw`.
 */
export function rehypeEscapeUnknownRawTags() {
  return (tree: WalkableNode) => {
    const walk = (node: WalkableNode): void => {
      if (
        node.type === 'raw' &&
        typeof node.value === 'string' &&
        node.value.includes('<')
      ) {
        node.value = escapeUnknownRawTags(node.value);
      }
      node.children?.forEach(walk);
    };
    walk(tree);
  };
}
